/**
 * `--login` — re-authenticate only.
 *
 * Expired refresh tokens, tenant Conditional Access changes, or a cleared
 * keychain used to force users through the full 7-step setup wizard just to
 * sign in again. This command loads the existing configuration, clears the
 * cached tokens, runs the device-code sign-in, and verifies the result the
 * same way the MCP server will consume it — nothing else.
 */

import { join } from "node:path";
import { existsSync, unlinkSync } from "node:fs";
import { loadConfig, getConfigDir } from "../config/loader.js";
import { createAuthProviderFromConfig } from "../auth/device-code.js";
import { RESOURCES } from "../auth/types.js";
import { TOKEN_CACHE_FILE_NAME, ACCOUNT_CACHE_FILE_NAME } from "../utils/constants.js";
import { quietInteractiveLogging } from "../utils/logger.js";
import { deleteSecureTokenCache } from "../auth/token-cache.js";

const c = {
  reset: "\x1b[0m",
  bold: "\x1b[1m",
  dim: "\x1b[2m",
  cyan: "\x1b[36m",
  green: "\x1b[32m",
  red: "\x1b[31m",
};

function print(message: string): void {
  console.log(message);
}

async function clearCachedTokens(): Promise<void> {
  const configDir = getConfigDir();
  // The platform secure store (keychain/DPAPI) is where the tokens actually
  // live on macOS/Windows — the files are signal/fallback artifacts. --login
  // promises a fresh sign-in, so clear the store too (best-effort).
  await deleteSecureTokenCache(join(configDir, TOKEN_CACHE_FILE_NAME));
  for (const name of [TOKEN_CACHE_FILE_NAME, ACCOUNT_CACHE_FILE_NAME]) {
    const path = join(configDir, name);
    if (existsSync(path)) {
      try {
        unlinkSync(path);
      } catch {
        // best-effort — a locked file just means the old cache lingers
      }
    }
  }
}

/** Returns the process exit code. */
export async function runLogin(): Promise<number> {
  quietInteractiveLogging();
  print("");
  print(`${c.bold}Power Automate MCP — Sign In${c.reset}`);
  print("");

  let clientId: string;
  let tenantId: string | null;
  try {
    const config = loadConfig();
    clientId = config.auth.clientId;
    tenantId = config.auth.tenantId;
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    print(`${c.red}No usable configuration found.${c.reset}`);
    print(`${c.dim}${msg.split("\n")[0]}${c.reset}`);
    print("");
    print(`Run ${c.cyan}powerautomate-mcp --setup${c.reset} for first-time setup.`);
    return 1;
  }

  await clearCachedTokens();
  print(`${c.dim}Sign in with your Microsoft work account — a device code will be shown.${c.reset}`);
  print("");

  try {
    const provider = await createAuthProviderFromConfig(clientId, tenantId);
    await provider.getTokenForResource(RESOURCES.FLOW_SERVICE);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    print(`${c.red}Sign-in failed:${c.reset} ${msg.split("\n")[0]}`);
    return 1;
  }

  // Verify the way the MCP server will consume it: a FRESH silent-only
  // provider must find the persisted tokens.
  try {
    const verify = await createAuthProviderFromConfig(clientId, tenantId, undefined, true);
    await verify.getTokenForResource(RESOURCES.FLOW_SERVICE);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    print(`${c.red}Signed in, but the token did not persist:${c.reset} ${msg.split("\n")[0]}`);
    print(`${c.dim}Run ${c.cyan}powerautomate-mcp --doctor${c.dim} for a full diagnosis.${c.reset}`);
    return 1;
  }

  print("");
  print(`${c.green}${c.bold}Signed in.${c.reset} ${c.dim}Restart your AI app if it was reporting sign-in errors.${c.reset}`);
  return 0;
}
